import { useQueryClient } from "@tanstack/react-query";
import { QuickSubmitForm } from "../../components";

import { filterConstants } from "../../constants";
import { productStatus } from "../../enums";
import {
  useQuickSubmitFormHelper,
  useEditItemProperties,
  useGetPageQueryParams,
  useMerchantApiQueryWithParams,
  useQueryString,
  useReactRouterLink,
} from "../../hooks";
import {
  IQueryStringNavLinks,
  ITableHeader,
  IViewProduct,
  IPageAndSearchRequest,
  PagedMeta,
} from "../../interfaces-and-types";
import { convertObjectToFormData } from "../../utils";
import { View } from "./View";

interface IProductQueryMeta {
  status: string;
}

export interface IProductViewProps {
  data: IViewProduct[];
  headers: ITableHeader[];
  isLoading: boolean;
  isError: boolean;
  totalCount: number;
  queryStringTabs: IQueryStringNavLinks;

  // modals
  openCreateNewModal: () => void;
  openCreatePaymentLinkModal: () => void;
  openDetailsModal: (data: any) => void;
  refreshTable: () => void;

  // extra buttons
  onExport: () => void;
}

export const Products = () => {
  const queryClient = useQueryClient();
  const query = useQueryString();
  const status = query.get(filterConstants.statusKey);
  const { page, page_size, search } = useGetPageQueryParams();

  const { navigate, navigateToRoutePreserveParams } = useReactRouterLink();

  const {
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
    data: products,
    meta: productsMeta,
    refetch: refetchProducts,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & IProductQueryMeta,
    IViewProduct[],
    PagedMeta
  >([
    "getProducts",
    {
      page: (page && Number(page)) || 1,
      page_size: (page_size && Number(page_size)) || 10,
      search,
      status: status || "active",
    },
  ]);

  const headers: ITableHeader[] = [
    {
      key: "product_icon",
      header: "",
    },
    {
      key: "product_name",
      header: "Name",
    },
    {
      key: "price",
      header: "Price",
    },
    {
      key: "payout_tag",
      header: "Payout Acct.",
    },
    // {
    //   key: "status",
    //   header: "Status",
    // },
    {
      key: "description",
      header: "Description",
    },
    {
      key: "created_at",
      header: "Created",
    },
    {
      key: "edited_at",
      header: "Last Updated",
    },
    {
      key: "more",
      header: "",
    },
  ];

  const queryStringTabs: IQueryStringNavLinks = {
    query: filterConstants.statusKey,
    options: [
      {
        key: "Available",
        value: "active",
      },
      // {
      //   key: "Staging",
      //   value: "staging",
      // },
      {
        key: "Archived",
        value: "disabled",
      },
    ],
  };

  const onExport = () => {
    alert("exporting data...");
  };

  const refreshTable = () => refetchProducts();

  // const refreshTable = () => {
  //   queryClient.invalidateQueries([
  //     "getProducts",
  //     {
  //       page: (page && Number(page)) || 1,
  //       page_size: (page_size && Number(page_size)) || 10,
  //       search,
  //       status: status || "active",
  //     },
  //   ]);
  // };

  // insert the functions to open edit/delete modals
  const productsWithLinks: IViewProduct[] | undefined = products?.map((p) => ({
    ...p,
    openEditModal: () => navigateToRoutePreserveParams(`${p.id}/edit`),
    openDeleteModal: () => openDeleteModal(p.id, p.is_delete_locked),
    archiveProduct: () =>
      openArchiveModal(
        p.id,
        convertObjectToFormData(p, {
          ...p,
          status:
            p.status === productStatus.active
              ? productStatus.disabled
              : productStatus.active,
        }),
        false /*p.isEditLocked*/,
        p.status === productStatus.disabled
      ),
  }));

  const openDetailsModal = (id: any) =>
    navigateToRoutePreserveParams(`${id}/details`);

  const openCreateNewModal = () => {
    navigateToRoutePreserveParams(`create`);
  };

  const openCreatePaymentLinkModal = () =>
    navigate("/payments/payment-links/create");

  const {
    isModalOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,

    isItemDeleteLocked,

    isLoading: isLoadingDeleteItem,
    isSuccess: isSuccessDeleteItem,
    mutate: deleteItem,
  } = useQuickSubmitFormHelper("deleteProduct", refreshTable);

  const {
    isEditModalOpen: isArchiveModalOpen,
    openEditModal: openArchiveModal,
    closeEditModal: closeArchiveModal,

    isItemEditLocked,

    isLoadingEditItem: isLoadingArchiveItem,
    isSuccessEditItem: isSuccessArchiveItem,
    editItem: archiveItem,
  } = useEditItemProperties("editProduct", refreshTable);

  return (
    <>
      <View
        data={productsWithLinks || []}
        headers={headers}
        isLoading={isLoadingProducts}
        isError={isErrorProducts}
        totalCount={productsMeta?.page_meta.item_count || 0}
        queryStringTabs={queryStringTabs}
        onExport={onExport}
        openCreateNewModal={openCreateNewModal}
        openCreatePaymentLinkModal={openCreatePaymentLinkModal}
        openDetailsModal={openDetailsModal}
        refreshTable={refreshTable}
      />

      <QuickSubmitForm
        formTitle="Delete product"
        isModalOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
        isDeleteLocked={isItemDeleteLocked}
        isSubmitting={isLoadingDeleteItem}
        isSuccess={isSuccessDeleteItem}
        onButtonClick={deleteItem}
        confirmationMessage={
          <>
            <h4>Are you sure you want to delete this product?</h4>
            <br />

            <span className="text-danger">
              (NOTE: This action is irreversible)
            </span>
          </>
        }
      />

      <QuickSubmitForm
        formTitle="Archive product"
        buttonText="Archive"
        buttonKind="secondary"
        isModalOpen={isArchiveModalOpen}
        closeModal={closeArchiveModal}
        isDeleteLocked={isItemEditLocked}
        isSubmitting={isLoadingArchiveItem}
        isSuccess={isSuccessArchiveItem}
        onButtonClick={() => archiveItem()}
        confirmationMessage={
          <>
            <h4>Are you sure you want to archive this product?</h4>
            <br />

            <span className="text-danger">
              (NOTE: You may restore it at any time)
            </span>
          </>
        }
      />
    </>
  );
};
